import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import axiosInstance from './axiosInstance';
import './styles/UserProfile.css';

const UserProfile = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [functionRoutes, setFunctionRoutes] = useState({});

  /* Fetch function definitions from API */
  useEffect(() => {
    axiosInstance.get('/function-definitions')
      .then(({ data }) => {
        const routes = {};
        (data.functions || [])
          .filter(fn => fn.enabled && fn.category !== 'Admin')
          .forEach(fn => { routes[fn.code] = { path: fn.route, label: fn.name }; });
        setFunctionRoutes(routes);
      })
      .catch(err => console.error('Failed to load function definitions:', err));
  }, []);

  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '' });
  const [preferences, setPreferences] = useState({
    theme: 'dark',
    defaultLandingPage: '/main-homepage',
    sidebarCollapsed: false,
    defaultCompany: '',
    emailNotifications: true,
  });
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [prefsMsg, setPrefsMsg] = useState({ type: '', text: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/user-profile');
      setProfileData(data);
      setProfileForm({ firstName: data.firstName || '', lastName: data.lastName || '' });
      setPreferences({
        theme: data.preferences?.theme || 'dark',
        defaultLandingPage: data.preferences?.defaultLandingPage || '/main-homepage',
        sidebarCollapsed: data.preferences?.sidebarCollapsed || false,
        defaultCompany: data.preferences?.defaultCompany || '',
        emailNotifications: data.preferences?.emailNotifications !== false,
      });
    } catch (err) {
      setProfileMsg({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setProfileMsg({ type: '', text: '' });
      await axiosInstance.put('/user-profile', profileForm);
      await refreshUser();
      setProfileMsg({ type: 'success', text: 'Profile updated successfully' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update profile' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setSavingPrefs(true);
      setPrefsMsg({ type: '', text: '' });
      const { data } = await axiosInstance.put('/user-profile/preferences', preferences);
      setPreferences(prev => ({ ...prev, ...data }));
      await refreshUser();
      setPrefsMsg({ type: 'success', text: 'Preferences saved successfully' });
    } catch (err) {
      setPrefsMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save preferences' });
    } finally {
      setSavingPrefs(false);
    }
  };

  /* Build landing page options based on user's allowed functions */
  const getLandingPageOptions = () => {
    const options = [{ value: '/main-homepage', label: 'Home' }];
    const codes = Object.keys(functionRoutes);
    for (const code of codes) {
      const hasAccess = user?.isSuperAdmin || user?.authorizedForAll || user?.allowedFunctions?.includes(code);
      if (hasAccess) {
        options.push({ value: functionRoutes[code].path, label: functionRoutes[code].label });
      }
    }
    return options;
  };

  const getRoleBadge = () => {
    if (profileData?.isSuperAdmin) return { text: 'Super Admin', className: 'up-badge-super' };
    if (profileData?.role === 'admin') return { text: 'Admin', className: 'up-badge-admin' };
    if (profileData?.role === 'sales') return { text: 'Sales', className: 'up-badge-sales' };
    if (profileData?.role === 'user') return { text: 'User', className: 'up-badge-user' };
    return { text: profileData?.role || '', className: '' };
  };

  if (loading) {
    return (
      <div className="up-page">
        <div className="up-loading">Loading profile...</div>
      </div>
    );
  }

  const roleBadge = getRoleBadge();
  const companyList = profileData?.companyAccess || [];
  const landingOptions = getLandingPageOptions();

  return (
    <div className="up-page">
      <button className="up-back" onClick={() => navigate('/main-homepage')}>
        <span className="up-back-arrow">&larr;</span> Back
      </button>
      <div className="up-header">
        <h1>My Profile</h1>
        <p className="up-subtitle">{profileData?.email}</p>
      </div>

      <div className="up-grid">
        {/* ── Profile Card ─────────────────────────── */}
        <div className="up-card">
          <h2 className="up-card-title">Profile Information</h2>

          {profileMsg.text && (
            <div className={`up-msg up-msg-${profileMsg.type}`}>{profileMsg.text}</div>
          )}

          <div className="up-field">
            <label className="up-label">Email</label>
            <div className="up-readonly">{profileData?.email}</div>
          </div>

          <div className="up-field">
            <label className="up-label">First Name</label>
            <input
              type="text"
              className="up-input"
              autoComplete="off"
              value={profileForm.firstName}
              onChange={e => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
            />
          </div>

          <div className="up-field">
            <label className="up-label">Last Name</label>
            <input
              type="text"
              className="up-input"
              autoComplete="off"
              value={profileForm.lastName}
              onChange={e => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
            />
          </div>

          <div className="up-field">
            <label className="up-label">Role</label>
            <div className="up-readonly">
              <span className={`up-badge ${roleBadge.className}`}>{roleBadge.text}</span>
            </div>
          </div>

          {profileData?.saasReadOnly && (
            <div className="up-field">
              <div className="up-msg up-msg-warning">
                ⚠️ Your SOLUM SaaS account is set to <strong>Read Only</strong>. You can view data but cannot make changes (assign labels, page flips, etc.). Contact your SOLUM administrator to remove this restriction.
              </div>
            </div>
          )}

          {companyList.length > 0 && (
            <div className="up-field">
              <label className="up-label">Company Access</label>
              <div className="up-company-list">
                {companyList.map(c => (
                  <span key={c} className="up-company-tag">{c}</span>
                ))}
              </div>
            </div>
          )}

          <button className="up-btn up-btn-primary" onClick={handleSaveProfile} disabled={savingProfile}>
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        {/* ── Preferences Card ─────────────────────── */}
        <div className="up-card">
          <h2 className="up-card-title">Preferences</h2>

          {prefsMsg.text && (
            <div className={`up-msg up-msg-${prefsMsg.type}`}>{prefsMsg.text}</div>
          )}

          {/* Default Landing Page */}
          <div className="up-field">
            <label className="up-label">Default Landing Page</label>
            <select
              className="up-select"
              value={preferences.defaultLandingPage}
              onChange={e => setPreferences(prev => ({ ...prev, defaultLandingPage: e.target.value }))}
            >
              {landingOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="up-hint">Page to load after login</p>
          </div>

          {/* Default Company */}
          {companyList.length > 1 && (
            <div className="up-field">
              <label className="up-label">Default Company</label>
              <select
                className="up-select"
                value={preferences.defaultCompany}
                onChange={e => setPreferences(prev => ({ ...prev, defaultCompany: e.target.value }))}
              >
                <option value="">No default</option>
                {companyList.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <p className="up-hint">Pre-select this company in tools</p>
            </div>
          )}

          {/* Sidebar Collapsed */}
          <div className="up-field">
            <div className="up-toggle-field">
              <label className="up-label">Start with sidebar collapsed</label>
              <button
                type="button"
                className={`up-toggle ${preferences.sidebarCollapsed ? 'up-toggle-on' : ''}`}
                onClick={() => setPreferences(prev => ({ ...prev, sidebarCollapsed: !prev.sidebarCollapsed }))}
                aria-label="Toggle sidebar collapsed"
              >
                <span className="up-toggle-knob" />
              </button>
            </div>
          </div>

          {/* Email Notifications */}
          <div className="up-field">
            <div className="up-toggle-field">
              <label className="up-label">Email notifications</label>
              <button
                type="button"
                className={`up-toggle ${preferences.emailNotifications ? 'up-toggle-on' : ''}`}
                onClick={() => setPreferences(prev => ({ ...prev, emailNotifications: !prev.emailNotifications }))}
                aria-label="Toggle email notifications"
              >
                <span className="up-toggle-knob" />
              </button>
            </div>
            <p className="up-hint">Receive email alerts for digital signage, uptime reports, etc.</p>
          </div>

          <button className="up-btn up-btn-primary" onClick={handleSavePreferences} disabled={savingPrefs}>
            {savingPrefs ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
